import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { findRegimen, planRegistry, type PlanId, type Regimen } from "@/data/meal-plan";
import type {
  ChoiceGroupSelections,
  DayOfWeek,
  RegimenPresets,
  WeekPlan,
} from "@/types/meal-plan-state";
import { createEmptyWeek } from "@/types/meal-plan-state";
import {
  clearMeals,
  copyMealsFromDay,
  createEmptyDaySelections,
  setDayMealMenu,
} from "@/lib/random-plan";
import { migrateStoredWeek, prepareLoadedWeek } from "@/lib/plan-switch";
import {
  fetchWeekList,
  fetchWeekSnapshot,
  saveWeekVersioned,
  type WeekEditor,
  type WeekSnapshot,
  type WeekSummary,
} from "@/lib/api";
import { fetchOwnerContext, ownerBasePath, ownerScope, type WeekOwner } from "@/lib/week-owner";
import { isSessionEnding, registerPendingSaveFlush, reportLostFriendEdit, trackLeavingFlush } from "@/lib/pending-saves";
import { rebaseWeek } from "@/lib/week-sync";
import { WeekSaveQueue, type SaveStatus } from "@/lib/week-save-queue";
import { EMPTY_WEEK_META, type WeekMeta } from "@/lib/edit-attribution";
import { getTodayDate } from "@/lib/week-utils";
import { useAuth } from "@/context/auth-context";

// ── State ──

interface AppState {
  /**
   * The week owner's plan regimens, for selectors (presets, day regimen
   * picker). Resolve a day's regimen with `findRegimen` instead: days keep
   * regimens of plans the owner has since switched away from.
   */
  regimens: Regimen[];
  /** The owner's plan; null until a friend's plan has loaded (INIT). */
  planId: PlanId | null;
  /** The owner's weekday presets, as seen under `planId`. */
  presets: RegimenPresets;
  currentWeekId: string;
  weekPlan: WeekPlan;
  weekList: WeekSummary[];
  loading: boolean;
  /** True once a week has been explicitly loaded via navigateToWeek */
  weekLoaded: boolean;
  /**
   * The week `weekPlan` belongs to ("" before the first load). It lags
   * `currentWeekId` while another week loads; autosave keys off it.
   */
  loadedWeekId: string;
  /** Presets and the week list have loaded (INIT). */
  initialized: boolean;
  /** INIT or the current week failed to load; the layout offers a retry. */
  loadError: boolean;
  saveStatus: SaveStatus;
  /** Days of the loaded week where another save won over my unsaved change (toast, then acknowledged). */
  lastConflict: WeekConflict | null;
  /** The loaded week's attribution, as the server last told us ("X actualizó tu semana", conflict toast). */
  weekMeta: WeekMeta;
}

export interface WeekConflict {
  weekId: string;
  days: DayOfWeek[];
  /** Per day, the meal indices that clashed when not the whole day did (names them in the toast). */
  meals?: Partial<Record<DayOfWeek, number[]>>;
  at: number;
  /** Who made the version that won, when the server said. */
  editor: WeekEditor | null;
}

const EMPTY_PRESETS: RegimenPresets = {
  mon: null, tue: null, wed: null, thu: null,
  fri: null, sat: null, sun: null,
};

function initialState(owner: WeekOwner): AppState {
  // My plan is known from the session; a friend's arrives with INIT.
  const planId = owner.kind === "self" ? owner.planId : null;
  return {
    regimens: planId ? planRegistry[planId].regimens : [],
    planId,
    presets: EMPTY_PRESETS,
    currentWeekId: "",
    weekPlan: createEmptyWeek(),
    weekList: [],
    loading: true,
    weekLoaded: false,
    loadedWeekId: "",
    initialized: false,
    loadError: false,
    saveStatus: "idle",
    lastConflict: null,
    weekMeta: EMPTY_WEEK_META,
  };
}

// ── Actions ──

type Action =
  | { type: "INIT"; planId: PlanId; presets: RegimenPresets; weekList: WeekSummary[] }
  | { type: "INIT_FAILED" }
  /**
   * The owner's plan changed (I switched plans, or a friend's refetch found a
   * new one): new selector regimens and the presets as seen under it.
   */
  | { type: "SET_PLAN"; planId: PlanId; presets: RegimenPresets }
  | { type: "SET_PRESET"; day: DayOfWeek; regimenId: Regimen["id"] }
  /** Show a week fetched from the server (also used, without a spinner, by refetch-on-focus). */
  | { type: "LOAD_WEEK"; weekId: string; plan: WeekPlan; conflict: WeekConflict | null; meta: WeekMeta }
  | { type: "LOAD_FAILED"; weekId: string }
  | { type: "NAVIGATE_TO_WEEK"; weekId: string }
  | { type: "SET_WEEK_LIST"; weekList: WeekSummary[] }
  | { type: "SET_LOADING"; loading: boolean }
  | { type: "SET_SAVE_STATUS"; status: SaveStatus }
  /** A save or a 409 of `weekId` reported its latest attribution (ignored unless it's the loaded week). */
  | { type: "SET_WEEK_META"; weekId: string; meta: WeekMeta }
  /**
   * A save hit a 409: rebase the shown week (the reducer's latest state, so
   * edits not yet queued are kept) from `base` onto the server's `theirs`.
   */
  | { type: "REBASE_WEEK"; weekId: string; base: WeekPlan; theirs: WeekPlan; conflict: WeekConflict | null }
  | { type: "ACK_CONFLICT" }
  /** Replace the whole week: "edit" for user actions (copy, undo), "system" otherwise. */
  | { type: "SET_WEEK_PLAN"; plan: WeekPlan; origin: "edit" | "system" }
  // Existing week-plan actions
  | { type: "SET_REGIMEN"; day: DayOfWeek; regimenId: Regimen["id"] | null }
  | {
      type: "SET_SELECTION";
      day: DayOfWeek;
      mealIndex: number;
      choiceGroupIndex: number;
      selections: ChoiceGroupSelections;
    }
  | {
      type: "SET_MEAL_SELECTIONS";
      day: DayOfWeek;
      mealIndex: number;
      selections: import("@/types/meal-plan-state").MealSelections;
    }
  | { type: "TOGGLE_SKIP"; day: DayOfWeek; mealIndex: number; choiceGroupIndex: number }
  | { type: "CLEAR_DAY"; day: DayOfWeek }
  /** Clear some meals of a day (selections, menus, notes); the rest of the day stays. */
  | { type: "CLEAR_MEALS"; day: DayOfWeek; mealIndices: number[] }
  | { type: "SET_MEAL_NOTE"; day: DayOfWeek; mealIndex: number; note: string }
  /** Replace one day (Comidas computes it with the pure helpers in `comidas-day.ts`). */
  | { type: "SET_DAY_PLAN"; day: DayOfWeek; plan: import("@/types/meal-plan-state").DayPlan }
  /** Choose (or clear with null) a menu meal's menu; its selections restart from the menu's defaults. */
  | { type: "SET_MEAL_MENU"; day: DayOfWeek; mealIndex: number; menuId: string | null }
  | { type: "DUPLICATE_MEALS"; day: DayOfWeek; sourcePlan: import("@/types/meal-plan-state").DayPlan; mealIndices: number[] }
  | { type: "DUPLICATE_WEEK"; selections: Record<DayOfWeek, { sourcePlan: import("@/types/meal-plan-state").DayPlan; mealIndices: number[] } | null> };

/** Deep-clone a DayPlan and strip the per-slot `eaten` flags so duplicated meals start unchecked. */
export function cloneSourceForDuplication(
  sourcePlan: import("@/types/meal-plan-state").DayPlan,
): import("@/types/meal-plan-state").DayPlan {
  const cloned: import("@/types/meal-plan-state").DayPlan = JSON.parse(JSON.stringify(sourcePlan));
  if (cloned.meals) {
    for (const meal of cloned.meals) {
      for (const cg of meal) {
        delete cg.eaten;
      }
    }
  }
  return cloned;
}

function reducer(state: AppState, action: Action): AppState {
  switch (action.type) {
    case "INIT":
      return {
        ...state,
        planId: action.planId,
        regimens: planRegistry[action.planId].regimens,
        presets: action.presets ?? EMPTY_PRESETS,
        weekList: action.weekList ?? [],
        loading: false,
        initialized: true,
        loadError: false,
      };

    case "INIT_FAILED":
      return { ...state, loading: false, loadError: true };

    case "SET_PLAN":
      return { ...state, planId: action.planId, regimens: planRegistry[action.planId].regimens, presets: action.presets };

    case "SET_PRESET":
      return {
        ...state,
        presets: { ...state.presets, [action.day]: action.regimenId },
      };

    case "LOAD_WEEK":
      return {
        ...state,
        currentWeekId: action.weekId,
        loadedWeekId: action.weekId,
        weekPlan: action.plan,
        loading: false,
        weekLoaded: true,
        loadError: false,
        lastConflict: action.conflict ?? state.lastConflict,
        weekMeta: action.meta,
      };

    case "LOAD_FAILED":
      if (action.weekId !== state.currentWeekId) return state;
      return { ...state, loading: false, weekLoaded: false, loadError: true };

    case "NAVIGATE_TO_WEEK":
      return { ...state, currentWeekId: action.weekId, loading: true, weekLoaded: false, loadError: false };

    case "SET_SAVE_STATUS":
      return state.saveStatus === action.status ? state : { ...state, saveStatus: action.status };

    case "SET_WEEK_META": {
      const { editedBy, editedAt, plannerEdit } = action.meta;
      const current = state.weekMeta;
      if (action.weekId !== state.loadedWeekId) return state;
      if (
        editedAt === current.editedAt &&
        editedBy?.id === current.editedBy?.id &&
        plannerEdit?.at === current.plannerEdit?.at &&
        plannerEdit?.by.id === current.plannerEdit?.by.id
      ) {
        return state;
      }
      return { ...state, weekMeta: action.meta };
    }

    case "REBASE_WEEK": {
      if (action.weekId !== state.loadedWeekId) return state;
      return {
        ...state,
        weekPlan: rebaseWeek(action.base, state.weekPlan, action.theirs).plan,
        // Toast only while the week is still the one being shown.
        lastConflict: (action.weekId === state.currentWeekId && action.conflict) || state.lastConflict,
      };
    }

    case "ACK_CONFLICT":
      return state.lastConflict ? { ...state, lastConflict: null } : state;

    case "SET_WEEK_PLAN":
      return { ...state, weekPlan: action.plan };

    case "SET_WEEK_LIST":
      return { ...state, weekList: action.weekList };

    case "SET_LOADING":
      return { ...state, loading: action.loading };

    // ── Week plan mutations (same as before) ──

    case "SET_REGIMEN": {
      const regimen = findRegimen(action.regimenId);
      return {
        ...state,
        weekPlan: {
          ...state.weekPlan,
          [action.day]: {
            regimenId: action.regimenId,
            meals: regimen ? createEmptyDaySelections(regimen) : null,
          },
        },
      };
    }
    case "SET_SELECTION": {
      const dayPlan = state.weekPlan[action.day];
      if (!dayPlan.meals) return state;
      const meals = dayPlan.meals.map((meal, mi) =>
        mi === action.mealIndex
          ? meal.map((cg, ci) =>
              ci === action.choiceGroupIndex ? action.selections : cg,
            )
          : meal,
      );
      return { ...state, weekPlan: { ...state.weekPlan, [action.day]: { ...dayPlan, meals } } };
    }
    case "SET_MEAL_SELECTIONS": {
      const dayPlan = state.weekPlan[action.day];
      if (!dayPlan.meals) return state;
      const meals = dayPlan.meals.map((meal, mi) =>
        mi === action.mealIndex ? action.selections : meal,
      );
      return { ...state, weekPlan: { ...state.weekPlan, [action.day]: { ...dayPlan, meals } } };
    }
    case "TOGGLE_SKIP": {
      const dayPlan = state.weekPlan[action.day];
      if (!dayPlan.meals) return state;
      const meals = dayPlan.meals.map((meal, mi) =>
        mi === action.mealIndex
          ? meal.map((cg, ci) =>
              ci === action.choiceGroupIndex
                ? { selections: cg.skipped ? cg.selections : [], skipped: !cg.skipped }
                : cg,
            )
          : meal,
      );
      return { ...state, weekPlan: { ...state.weekPlan, [action.day]: { ...dayPlan, meals } } };
    }
    case "CLEAR_DAY": {
      const dayPlan = state.weekPlan[action.day];
      const regimen = findRegimen(dayPlan.regimenId);
      if (!regimen) return state;
      return {
        ...state,
        weekPlan: {
          ...state.weekPlan,
          [action.day]: {
            regimenId: regimen.id,
            meals: createEmptyDaySelections(regimen),
          },
        },
      };
    }
    case "CLEAR_MEALS": {
      const dayPlan = state.weekPlan[action.day];
      const regimen = findRegimen(dayPlan.regimenId);
      if (!regimen || !dayPlan.meals) return state;
      return {
        ...state,
        weekPlan: { ...state.weekPlan, [action.day]: clearMeals(dayPlan, regimen, action.mealIndices) },
      };
    }
    case "SET_MEAL_NOTE": {
      const dayPlan = state.weekPlan[action.day];
      const notes = { ...dayPlan.mealNotes };
      if (action.note.trim()) {
        notes[action.mealIndex] = action.note.trim();
      } else {
        delete notes[action.mealIndex];
      }
      return {
        ...state,
        weekPlan: {
          ...state.weekPlan,
          [action.day]: { ...dayPlan, mealNotes: notes },
        },
      };
    }
    case "SET_DAY_PLAN":
      return state.weekPlan[action.day] === action.plan
        ? state
        : { ...state, weekPlan: { ...state.weekPlan, [action.day]: action.plan } };
    case "SET_MEAL_MENU": {
      const dayPlan = state.weekPlan[action.day];
      const meal = findRegimen(dayPlan.regimenId)?.meals[action.mealIndex];
      if (!meal?.menus || !dayPlan.meals) return state;
      return {
        ...state,
        weekPlan: {
          ...state.weekPlan,
          [action.day]: setDayMealMenu(dayPlan, meal, action.mealIndex, action.menuId),
        },
      };
    }
    case "DUPLICATE_MEALS": {
      const { day, sourcePlan, mealIndices } = action;
      return {
        ...state,
        weekPlan: {
          ...state.weekPlan,
          [day]: copyMealsFromDay(
            state.weekPlan[day],
            cloneSourceForDuplication(sourcePlan),
            mealIndices,
          ),
        },
      };
    }
    case "DUPLICATE_WEEK": {
      const newWeekPlan = { ...state.weekPlan };
      const days: DayOfWeek[] = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

      for (const day of days) {
        const sel = action.selections[day];
        if (!sel) continue;

        newWeekPlan[day] = copyMealsFromDay(
          newWeekPlan[day],
          cloneSourceForDuplication(sel.sourcePlan),
          sel.mealIndices,
        );
      }

      return { ...state, weekPlan: newWeekPlan };
    }
  }
}

// ── Context ──

interface WeekPlanContextValue {
  state: AppState;
  dispatch: React.Dispatch<Action>;
  /** Whose weeks these are: mine, or a friend's (friend mode). */
  owner: WeekOwner;
  /** "/week" or "/friend/:id/week": prefix for links inside the owner's weeks. */
  basePath: string;
  /** "Comido" is the owner's own log: a planner sees it but can't tick it. */
  canMarkEaten: boolean;
  /** Save pending edits, then load `weekId` from the server. Never rejects. */
  navigateToWeek: (weekId: string) => Promise<void>;
  /**
   * Switch the user's plan: save pending edits, switch server-side, adopt the
   * new plan's regimens and presets, and reload the current week so its
   * untouched days from today on follow the new plan. Mine only: undefined
   * for a friend's weeks (a planner can't change their plan).
   */
  switchPlan: ((planId: PlanId) => Promise<void>) | undefined;
  /** Retry the queued saves after `saveStatus === "error"`. */
  retrySave: () => void;
  /** Drop unsaved edits ("Salir sin guardar"), so leaving doesn't save them after all. */
  discardPendingSaves: () => void;
  /** Retry loading after `loadError`. */
  retryLoad: () => void;
  /** Acknowledge `state.lastConflict` once it has been shown. */
  ackConflict: () => void;
}

const WeekPlanContext = createContext<WeekPlanContextValue | null>(null);

/** Actions that change the week because the user did something (saved as `kind: "edit"`). */
function isUserEdit(action: Action): boolean {
  switch (action.type) {
    case "SET_REGIMEN":
    case "SET_SELECTION":
    case "SET_MEAL_SELECTIONS":
    case "TOGGLE_SKIP":
    case "CLEAR_DAY":
    case "CLEAR_MEALS":
    case "SET_MEAL_NOTE":
    case "SET_DAY_PLAN":
    case "SET_MEAL_MENU":
    case "DUPLICATE_MEALS":
    case "DUPLICATE_WEEK":
      return true;
    case "SET_WEEK_PLAN":
      return action.origin === "edit";
    default:
      return false;
  }
}

/**
 * Week state plus versioned autosave.
 *
 * Saving (see `WeekSaveQueue`): every server copy the provider loads becomes
 * that week's base (version + plan). Whenever the shown week differs from its
 * base it is queued, debounced 300 ms, and saved with the base's version, one
 * request at a time. A 409 rebases onto the server copy (REBASE_WEEK) and
 * retries; an error keeps the queue and shows "No se pudo guardar".
 *
 * Loading: `navigateToWeek` saves what's queued, fetches the snapshot,
 * rebases a still-queued failed save onto it or else prepares it
 * (`prepareLoadedWeek`), and shows it. Anything preparing changed differs
 * from the base and is saved as `kind: "system"`; an unchanged reload saves
 * nothing. Coming back to the tab refetches without a spinner.
 *
 * `owner` is whose weeks these are. Everything that plans a week (adoption,
 * weeks built from presets, the regimen selector) uses the owner's plan and
 * presets from provider state, loaded at INIT. A friend's provider differs in
 * that it talks to `/api/friends/:id/...`, never saves a change nobody made
 * (just looking writes nothing), saves what's pending when it unmounts, and
 * refreshes the friend's plan when the tab comes back. Mount it with
 * `key={friendId}`: the owner is fixed for the provider's lifetime (my own
 * owner's `planId` may change; `switchPlan` handles that).
 */
export function WeekPlanProvider({ children, owner }: { children: ReactNode; owner: WeekOwner }) {
  const [state, rawDispatch] = useReducer(reducer, owner, initialState);
  const { setPlan } = useAuth();
  const ownerRef = useRef(owner);
  ownerRef.current = owner;
  const isSelf = owner.kind === "self";
  const [scope] = useState(() => ownerScope(owner));

  // Latest render's state, for callbacks that must stay stable.
  const stateRef = useRef(state);
  stateRef.current = state;
  // switchPlan sets these before the reload's render; renders keep them in sync.
  const presetsRef = useRef(state.presets);
  presetsRef.current = state.presets;
  const regimensRef = useRef(state.regimens);
  regimensRef.current = state.regimens;

  const initializedRef = useRef(false);
  /** A user edit was dispatched since the autosave effect last ran: the next save is `kind: "edit"`. */
  const userEditRef = useRef(false);
  /** Bumped on every user edit, synchronously at dispatch: lets a silent refetch notice edits made during its fetch. */
  const editSeqRef = useRef(0);
  /** Bumped by every navigateToWeek: a load whose token is stale is dropped. */
  const loadTokenRef = useRef(0);
  /**
   * The week that is loaded and on screen, or null while a week loads or after
   * a failed load. Set synchronously with the dispatches (renders may lag),
   * so refetch-on-focus never reloads a week that is being navigated away from.
   */
  const shownWeekRef = useRef<string | null>(null);
  const refetchingRef = useRef(false);

  const [saveQueue] = useState(
    () =>
      new WeekSaveQueue(
        {
          save: (weekId, plan, baseVersion, kind) => saveWeekVersioned(scope, weekId, plan, baseVersion, kind),
          onStatus: (status) => rawDispatch({ type: "SET_SAVE_STATUS", status }),
          onRebase: ({ weekId, base, theirs, conflictDays, conflictMeals, editor }) =>
            rawDispatch({
              type: "REBASE_WEEK",
              weekId,
              base,
              theirs,
              conflict:
                conflictDays.length > 0
                  ? { weekId, days: conflictDays, meals: conflictMeals, at: Date.now(), editor }
                  : null,
            }),
          onMeta: (weekId, meta) => rawDispatch({ type: "SET_WEEK_META", weekId, meta }),
          // Best effort: the week list is incidental to saves. Skipped while
          // logging out (the session is gone before it would answer).
          onSaved: () => {
            if (isSessionEnding()) return;
            fetchWeekList(scope).then(
              (weekList) => rawDispatch({ type: "SET_WEEK_LIST", weekList }),
              (error: unknown) => console.error("Failed to refresh week list", error),
            );
          },
        },
        undefined,
        // Just viewing a friend's week must write nothing (decision §8.1).
        { saveSystemChanges: owner.kind === "self" },
      ),
  );

  const dispatch = useCallback((action: Action) => {
    if (isUserEdit(action)) {
      userEditRef.current = true;
      editSeqRef.current += 1;
    }
    rawDispatch(action);
  }, []);

  // Autosave: queue the shown week whenever it changes. The queue ignores a
  // plan equal to the week's server copy, so loads and reloads save nothing
  // unless preparing the week changed it.
  useEffect(() => {
    const userEdit = userEditRef.current;
    userEditRef.current = false;
    if (state.loadedWeekId) saveQueue.enqueue(state.loadedWeekId, state.weekPlan, userEdit);
  }, [saveQueue, state.loadedWeekId, state.weekPlan]);

  /** Rejects if any edit is still unsaved afterwards (logout, plan switch). */
  const flushPendingSave = useCallback(() => saveQueue.flush(), [saveQueue]);

  useEffect(() => registerPendingSaveFlush(flushPendingSave), [flushPendingSave]);

  // Leaving: my weeks don't fire a debounced save after the provider is gone
  // (logout flushes first). A friend's provider unmounts whenever the planner
  // leaves friend mode (switcher, Salir, back button), so it saves now
  // (tracked, so a following flushPendingSaves waits for it). The back button
  // skips the "¿Salir igual?" guard, so a failure is reported to the navbar's
  // toast; after a 403 the revoked card already says it.
  useEffect(
    () => () => {
      if (isSelf || isSessionEnding()) saveQueue.cancelTimer();
      else
        trackLeavingFlush(saveQueue.flush()).catch((error: unknown) => {
          console.error("Unsaved changes left friend mode", error);
          if (stateRef.current.saveStatus !== "forbidden") reportLostFriendEdit(ownerRef.current.username);
        });
    },
    [saveQueue, isSelf],
  );

  const discardPendingSaves = useCallback(() => saveQueue.discard(), [saveQueue]);

  const retrySave = useCallback(() => {
    saveQueue.drain().catch((error: unknown) => console.error("Failed to save week plan", error));
  }, [saveQueue]);

  const init = useCallback(async () => {
    try {
      const [ownerContext, weekList] = await Promise.all([
        fetchOwnerContext(ownerRef.current),
        fetchWeekList(scope).catch((error: unknown) => {
          console.error("Failed to load week list", error);
          return [];
        }),
      ]);
      // The first week load (queued behind INIT) prepares with these.
      presetsRef.current = ownerContext.presets;
      regimensRef.current = planRegistry[ownerContext.planId].regimens;
      rawDispatch({ type: "INIT", planId: ownerContext.planId, presets: ownerContext.presets, weekList });
    } catch (error) {
      console.error("Failed to load the plan and presets", error);
      rawDispatch({ type: "INIT_FAILED" });
    }
  }, [scope]);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    void init();
  }, [init]);

  /**
   * Record `snapshot` as the week's base and show it, prepared (or with a
   * queued save rebased onto it). A silent refetch only migrates: re-adopting
   * with this tab's possibly stale plan would undo a plan switch made on
   * another device, and the two would overwrite each other on every focus.
   */
  const showSnapshot = useCallback(
    (weekId: string, snapshot: WeekSnapshot, silent = false) => {
      const { stored, conflictDays, conflictMeals, editor, meta } = saveQueue.load(weekId, snapshot);
      const plan =
        silent && stored
          ? migrateStoredWeek(stored)
          : prepareLoadedWeek(stored, weekId, getTodayDate(), regimensRef.current, presetsRef.current);
      // What preparing changed is a system save. No real edit can be pending
      // here (the spinner is up, or refetch bailed out on one); this only
      // clears a flag left by an edit that changed nothing.
      userEditRef.current = false;
      shownWeekRef.current = weekId;
      rawDispatch({
        type: "LOAD_WEEK",
        weekId,
        plan,
        conflict:
          conflictDays.length > 0 ? { weekId, days: conflictDays, meals: conflictMeals, at: Date.now(), editor } : null,
        meta,
      });
    },
    [saveQueue],
  );

  const navigateToWeek = useCallback(
    async (weekId: string): Promise<void> => {
      const token = ++loadTokenRef.current;
      shownWeekRef.current = null;
      rawDispatch({ type: "NAVIGATE_TO_WEEK", weekId });

      // Save first so the snapshot includes it. A failure keeps the save
      // queued; showSnapshot rebases it onto the snapshot.
      await saveQueue.drain().catch((error: unknown) => console.error("Failed to save week plan", error));
      if (token !== loadTokenRef.current) return;

      try {
        const snapshot = await fetchWeekSnapshot(scope, weekId);
        if (token !== loadTokenRef.current) return;
        showSnapshot(weekId, snapshot);
      } catch (error) {
        console.error("Failed to load week plan", error);
        if (token === loadTokenRef.current) rawDispatch({ type: "LOAD_FAILED", weekId });
      }
    },
    [saveQueue, showSnapshot, scope],
  );

  const retryLoad = useCallback(() => {
    const current = stateRef.current;
    if (!current.initialized) {
      rawDispatch({ type: "SET_LOADING", loading: true });
      void init();
    } else if (current.currentWeekId) {
      void navigateToWeek(current.currentWeekId);
    }
  }, [init, navigateToWeek]);

  /**
   * Back on the tab: pick up changes made elsewhere, without a spinner. With
   * saves pending, drain instead: a 409 merges the other device's changes.
   */
  const refetchCurrentWeek = useCallback(async () => {
    const weekId = shownWeekRef.current;
    if (!weekId || refetchingRef.current) return;
    if (saveQueue.hasPending(weekId)) {
      saveQueue.drain().catch((error: unknown) => console.error("Failed to save week plan", error));
      return;
    }

    const token = loadTokenRef.current;
    const editSeq = editSeqRef.current;
    refetchingRef.current = true;
    try {
      const snapshot = await fetchWeekSnapshot(scope, weekId);
      // A navigation, an edit or a save since the fetch started wins over it.
      if (token !== loadTokenRef.current || editSeq !== editSeqRef.current || saveQueue.hasPending(weekId)) return;
      if (snapshot.version === saveQueue.baseVersion(weekId)) return;
      showSnapshot(weekId, snapshot, true);
    } catch (error) {
      console.error("Failed to refresh week plan", error);
    } finally {
      refetchingRef.current = false;
    }
  }, [saveQueue, showSnapshot, scope]);

  /**
   * A friend may switch plans or presets while I plan for them: pick that up
   * when the tab comes back, so the regimen selector and new weeks follow.
   * Weeks already shown keep their days (like a refetch, nothing re-adopts).
   */
  const refreshOwnerContext = useCallback(async () => {
    const current = ownerRef.current;
    if (current.kind === "self" || !stateRef.current.initialized) return;
    try {
      const { planId, presets } = await fetchOwnerContext(current);
      const prev = stateRef.current;
      if (prev.planId === planId && JSON.stringify(prev.presets) === JSON.stringify(presets)) return;
      presetsRef.current = presets;
      regimensRef.current = planRegistry[planId].regimens;
      rawDispatch({ type: "SET_PLAN", planId, presets });
    } catch (error) {
      // A 403 here means the friendship ended; the next save reports it.
      console.error("Failed to refresh the friend's plan", error);
    }
  }, []);

  useEffect(() => {
    const drainNow = () => {
      if (saveQueue.hasPending()) {
        saveQueue.drain().catch((error: unknown) => console.error("Failed to save week plan", error));
      }
    };
    const onBack = () => {
      void refetchCurrentWeek();
      void refreshOwnerContext();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") onBack();
      // Phones may kill a backgrounded tab: don't wait out the debounce.
      else drainNow();
    };
    const onFocus = onBack;
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onFocus);
    window.addEventListener("online", drainNow);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", drainNow);
    };
  }, [saveQueue, refetchCurrentWeek, refreshOwnerContext]);

  const switchPlan = useCallback(
    async (planId: PlanId) => {
      await flushPendingSave();
      const presets = await setPlan(planId);
      // Update the refs now: the reload below must adopt into the new plan.
      presetsRef.current = presets;
      regimensRef.current = planRegistry[planId].regimens;
      rawDispatch({ type: "SET_PLAN", planId, presets });
      const weekId = stateRef.current.currentWeekId;
      if (weekId) await navigateToWeek(weekId);
    },
    [flushPendingSave, setPlan, navigateToWeek],
  );

  const ackConflict = useCallback(() => rawDispatch({ type: "ACK_CONFLICT" }), []);

  return (
    <WeekPlanContext.Provider
      value={{
        state,
        dispatch,
        owner,
        basePath: ownerBasePath(owner),
        canMarkEaten: isSelf,
        navigateToWeek,
        switchPlan: isSelf ? switchPlan : undefined,
        retrySave,
        discardPendingSaves,
        retryLoad,
        ackConflict,
      }}
    >
      {children}
    </WeekPlanContext.Provider>
  );
}

export function useWeekPlan() {
  const ctx = useContext(WeekPlanContext);
  if (!ctx) throw new Error("useWeekPlan must be used within WeekPlanProvider");
  return ctx;
}

/** The week context when inside a provider, else null (components also rendered outside one). */
export function useOptionalWeekPlan() {
  return useContext(WeekPlanContext);
}
